import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileJson } from 'lucide-react';
import { ENVIRONMENTS, systemConfig } from '@/config/systemConfig';

import {
  exportScopeTree,
  exportScopeTreeBatch,
  importScopeManifest,
  fetchPendingImports,
  fetchPendingImportDetail,
  approvePendingImport,
  rejectPendingImport,
} from '../../api/scopeMigrateApi';
import { generateMigrationSQL } from '../../api/generateMigrationSQL';
import { useTenantOverride } from '@/shared/auth/tenantContext';
import { useDeviceAuthFlow } from '@/hooks/useDeviceAuthFlow';
import { createRemoteBffClient, RemoteBffError } from '@/lib/http/remoteBff';

import type {
  ScopeManifest, ManifestScope, ManifestOutcome, ExportParams,
  ImportResult, EnvironmentDef, MigrationStep,
  PendingImportItem, QueueResult,
} from './types';
import { computePreviewSummary, ingestManifestFile, ingestManifestText, type IngestResult } from './utils';
import { useScopeSelection } from './hooks/useScopeSelection';
import { EnvDropdown, StepPill } from './components/shared';
import { SourceScopesPanel } from './components/SourceScopesPanel';
import { ExportPreviewOverlay } from './components/ExportPreviewOverlay';
import { ExportSummaryPopup } from './components/ExportSummaryPopup';
import { ManifestViewPanel } from './components/ManifestViewPanel';
import { ImportApprovalPopup } from './components/ImportApprovalPopup';
import { ImportProgressOverlay } from './components/ImportProgressOverlay';
import { PendingImportsPanel } from './components/PendingImportsPanel';
import { PushToRemoteSection } from './components/PushToRemoteSection';
import { PullFromRemoteSection } from './components/PullFromRemoteSection';
import { RemoteSnapshotBrowser } from './components/RemoteSnapshotBrowser';
import { usePullJob } from './hooks/usePullJob';
import { useRemoteSnapshot } from './hooks/useRemoteSnapshot';
import { upsertGrant, DEFAULT_GRANT_TTL } from '../../api/remoteAuthApi';

export default function ScopeMigrationPanel() {
  const [sourceEnv, setSourceEnv] = useState(systemConfig.scope_migration.default_source_env);
  const [targetEnv, setTargetEnv] = useState(systemConfig.scope_migration.default_target_env);
  // Centralized tenant context — sidebar TenantSelector is single source of truth
  const { tenants: tenantList, activeTenantId: ctxTenantId } = useTenantOverride();
  const activeTenantId: number | '' = ctxTenantId ?? '';
  const activeTid = activeTenantId !== '' ? activeTenantId : undefined;
  const selection = useScopeSelection(activeTenantId);
  const [exportStep, setExportStep] = useState<MigrationStep>('select');
  const [showExportSummary, setShowExportSummary] = useState(false);
  const [includeChildren, setIncludeChildren] = useState(true);
  const [filterMaxDepth, setFilterMaxDepth] = useState(0);
  const [includeOutcomes, setIncludeOutcomes] = useState(true);
  const [includeHotStream, setIncludeHotStream] = useState(true);
  const [targetTenantId, setTargetTenantId] = useState<number | ''>(activeTenantId);
  const [cloneStrategy, setCloneStrategy] = useState<'skip' | 'upsert' | 'overwrite'>('upsert');
  const [cloneResult, setCloneResult] = useState<ImportResult | null>(null);
  const [cloneDryRunResult, setCloneDryRunResult] = useState<ImportResult | null>(null);
  const [showImportApproval, setShowImportApproval] = useState(false);
  const [manifest, setManifest] = useState<ScopeManifest | null>(null);
  const [manifestText, setManifestText] = useState('');
  const [parseError, setParseError] = useState('');
  const [showUploadArea, setShowUploadArea] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [manifestSelection, setManifestSelection] = useState<Set<string>>(new Set());
  const [viewTab, setViewTab] = useState<'tree' | 'sql'>('tree');
  const [generatedSQL, setGeneratedSQL] = useState('');
  const [sqlCopied, setSqlCopied] = useState(false);
  const deviceAuth = useDeviceAuthFlow();
  const pullDeviceAuth = useDeviceAuthFlow();  // separate auth instance for pull-from-source
  const pullJob = usePullJob();
  const [showSnapshotBrowser, setShowSnapshotBrowser] = useState(false);
  const [pushStrategy, setPushStrategy] = useState<'skip' | 'upsert' | 'overwrite'>('upsert');
  const [pushResult, setPushResult] = useState<ImportResult | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);
  const [isPushing, setIsPushing] = useState(false);
  const queryClient = useQueryClient();

  // ── Service-to-service auth status for zero-interaction pull ────────
  const { data: serviceAuthStatus } = useQuery<{ available: boolean; method: string }>({
    queryKey: ['migrateServiceAuthStatus'],
    queryFn: async () => {
      const res = await fetch('/bff/orchestrator/migrate/scopes/service-auth-status', { credentials: 'include' });
      if (!res.ok) return { available: false, method: 'device_auth' };
      return res.json();
    },
    staleTime: 60_000,
    retry: false,
  });
  const serviceAuthAvailable = serviceAuthStatus?.available ?? false;

  // ── Pending imports (inbound queue) ─────────────────────────────────
  const { data: pendingImports = [], isLoading: pendingLoading } = useQuery<PendingImportItem[]>({
    queryKey: ['pendingImports'],
    queryFn: fetchPendingImports,
    refetchInterval: 15_000,
    staleTime: 10_000,
  });
  const [pendingReviewLoading, setPendingReviewLoading] = useState<string | null>(null);
  const [pendingRejectLoading, setPendingRejectLoading] = useState<string | null>(null);
  const [pendingApprovalId, setPendingApprovalId] = useState<string | null>(null);
  const [pendingApprovalManifest, setPendingApprovalManifest] = useState<ScopeManifest | null>(null);

  // ── Progress overlay state for long-running imports ──────────────────
  const [progressManifest, setProgressManifest] = useState<ScopeManifest | null>(null);
  const [progressResult, setProgressResult] = useState<ImportResult | null>(null);
  const [progressError, setProgressError] = useState<string | null>(null);
  const [progressActive, setProgressActive] = useState(false);

  const exportSummary = useMemo(() => {
    if (!manifest) return null;
    const nsSet = new Set(manifest.scopes.map((s: ManifestScope) => s.scope_namespace));
    const handlerSet = new Set((manifest.outcomes ?? []).map((o: ManifestOutcome) => o.handler_key));
    const allGcsFiles = [
      ...(manifest.gcs_files ?? []),
      ...(manifest.hot_archive_files ?? []),
    ];
    return {
      scopeCount: manifest.scopes.length,
      namespaceCount: nsSet.size,
      namespaces: Array.from(nsSet),
      outcomeCount: manifest.outcomes?.length ?? 0,
      handlerCount: handlerSet.size,
      handlers: Array.from(handlerSet).slice(0, 12),
      hotStreamCount: manifest.hot_stream?.length ?? 0,
      archiveFileCount: manifest.hot_archive_files?.length ?? 0,
      artifactFileCount: allGcsFiles.length,
      artifactTotalBytes: allGcsFiles.reduce((sum, f) => sum + (f.size_bytes ?? 0), 0),
    };
  }, [manifest]);

  useEffect(() => {
    if (!manifest) { setManifestSelection(new Set()); return; }
    const keys = new Set(manifest.scopes.map((s) => `${s.scope_namespace}\x00${s.scope_key}`));
    setManifestSelection(keys);
  }, [manifest]);

  const handleToggleManifestKey = useCallback((key: string) => {
    setManifestSelection((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const handleToggleManifestNamespace = useCallback((ns: string, select: boolean) => {
    if (!manifest) return;
    setManifestSelection((prev) => {
      const next = new Set(prev);
      for (const s of manifest.scopes) {
        const nk = `${s.scope_namespace}\x00${s.scope_key}`;
        if (s.scope_namespace === ns) { if (select) next.add(nk); else next.delete(nk); }
      }
      return next;
    });
  }, [manifest]);

  const filteredManifest = useMemo((): ScopeManifest | null => {
    if (!manifest) return null;
    if (manifestSelection.size === manifest.scopes.length) return manifest;
    const selectedScopes = manifest.scopes.filter(
      (s) => manifestSelection.has(`${s.scope_namespace}\x00${s.scope_key}`),
    );
    if (selectedScopes.length === 0) return null;
    const selectedKeys = new Set(selectedScopes.map((s) => `${s.scope_namespace}\x00${s.scope_key}`));
    return {
      ...manifest,
      scopes: selectedScopes,
      outcomes: (manifest.outcomes ?? []).filter(
        (o) => selectedKeys.has(`${o.scope_ref.scope_namespace}\x00${o.scope_ref.scope_key}`),
      ),
      hot_stream: (manifest.hot_stream ?? []).filter(
        (h) => selectedKeys.has(`${h.scope_ref.scope_namespace}\x00${h.scope_ref.scope_key}`),
      ),
      hot_archive_files: manifest.hot_archive_files,
    };
  }, [manifest, manifestSelection]);

  const exportMutation = useMutation({
    mutationFn: async () => {
      if (selection.selectedScopeIds.size === 0) throw new Error('No scopes selected');
      const ids = Array.from(selection.selectedScopeIds);

      // Single request — the orchestrator merges manifests server-side.
      // Avoids the N-request fan-out that overwhelmed browser connection limits.
      const params: Omit<ExportParams, 'root_scope_id'> = {
        include_children: includeChildren,
        include_outcomes: includeOutcomes,
        include_hot_stream: includeHotStream,
      };
      if (sourceEnv.trim()) params.source_env = sourceEnv.trim();
      if (filterMaxDepth > 0) params.max_depth = filterMaxDepth;
      if (activeTid) params.tenant_id = activeTid;

      // Use batch endpoint for multi-root exports, single for single root.
      if (ids.length === 1) {
        return exportScopeTree({ root_scope_id: ids[0], ...params });
      }
      return exportScopeTreeBatch(ids, params);
    },
    onSuccess: (data) => {
      setManifest(data);
      setShowUploadArea(false);
      setExportStep('done');
    },
  });

  const importDryRunMutation = useMutation({
    mutationFn: async () => {
      if (!filteredManifest) throw new Error('No manifest to import');
      if (!targetTenantId || targetTenantId <= 0) throw new Error('Select a target tenant');
      return importScopeManifest(filteredManifest, {
        strategy: cloneStrategy,
        dry_run: true,
        target_tenant_id: targetTenantId,
      });
    },
    onSuccess: (data) => setCloneDryRunResult(data),
  });

  const importConfirmMutation = useMutation({
    mutationFn: async () => {
      if (!filteredManifest) throw new Error('No manifest to import');
      if (!targetTenantId || targetTenantId <= 0) throw new Error('Select a target tenant');
      setProgressManifest(filteredManifest);
      setProgressResult(null);
      setProgressError(null);
      setProgressActive(true);
      return importScopeManifest(filteredManifest, {
        strategy: cloneStrategy,
        target_tenant_id: targetTenantId,
      });
    },
    onSuccess: (data) => {
      setProgressResult(data);
      setProgressActive(false);
      setCloneResult(data);
      setShowImportApproval(false);
      setCloneDryRunResult(null);
    },
    onError: (err: any) => {
      setProgressError(err?.message ?? String(err));
      setProgressActive(false);
    },
  });

  // ── Pending import approval (for queued inbound manifests) ──────────
  const pendingApprovalDryRunMutation = useMutation({
    mutationFn: async () => {
      if (!pendingApprovalId) throw new Error('No pending item selected');
      if (!targetTenantId || targetTenantId <= 0) throw new Error('Select a target tenant');
      return approvePendingImport(pendingApprovalId, {
        strategy: cloneStrategy,
        dry_run: true,
        target_tenant_id: targetTenantId,
      });
    },
    onSuccess: (data) => setCloneDryRunResult(data),
  });

  const pendingApprovalConfirmMutation = useMutation({
    mutationFn: async () => {
      if (!pendingApprovalId) throw new Error('No pending item selected');
      if (!targetTenantId || targetTenantId <= 0) throw new Error('Select a target tenant');
      const m = pendingApprovalManifest ?? filteredManifest;
      if (m) {
        setProgressManifest(m);
        setProgressResult(null);
        setProgressError(null);
        setProgressActive(true);
      }
      return approvePendingImport(pendingApprovalId, {
        strategy: cloneStrategy,
        target_tenant_id: targetTenantId,
      });
    },
    onSuccess: (data) => {
      setProgressResult(data);
      setProgressActive(false);
      setCloneResult(data);
      setShowImportApproval(false);
      setPendingApprovalId(null);
      setPendingApprovalManifest(null);
      setCloneDryRunResult(null);
      queryClient.invalidateQueries({ queryKey: ['pendingImports'] });
    },
    onError: (err: any) => {
      setProgressError(err?.message ?? String(err));
      setProgressActive(false);
    },
  });

  async function handlePendingReview(id: string) {
    setPendingReviewLoading(id);
    try {
      const detail = await fetchPendingImportDetail(id);
      setManifest(detail.manifest);
      setExportStep('done');
      setShowUploadArea(false);
    } catch { /* ignore */ }
    finally { setPendingReviewLoading(null); }
  }

  function handlePendingApprove(id: string) {
    const item = pendingImports.find((i) => i.id === id);
    setPendingApprovalId(id);
    setCloneDryRunResult(null);
    // If we already loaded the manifest via review, use it; otherwise fetch it.
    if (manifest && item) {
      setPendingApprovalManifest(manifest);
      setShowImportApproval(true);
    } else {
      setPendingReviewLoading(id);
      fetchPendingImportDetail(id)
        .then((detail) => {
          setManifest(detail.manifest);
          setPendingApprovalManifest(detail.manifest);
          setExportStep('done');
          setShowImportApproval(true);
        })
        .catch(() => { /* ignore */ })
        .finally(() => setPendingReviewLoading(null));
    }
  }

  async function handlePendingReject(id: string) {
    setPendingRejectLoading(id);
    try {
      await rejectPendingImport(id);
      queryClient.invalidateQueries({ queryKey: ['pendingImports'] });
    } catch { /* ignore */ }
    finally { setPendingRejectLoading(null); }
  }

  function tryParseManifest(raw: string) {
    setManifestText(raw);
    const { manifest: m, error } = ingestManifestText(raw);
    setParseError(error ?? '');
    setManifest(m);
  }

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    ingestManifestFile(file).then(({ manifest: m, error }) => {
      setParseError(error ?? '');
      if (m) { setManifest(m); setShowUploadArea(false); }
    });
    e.target.value = '';
  }

  /** Shared ingest callback — used by ManifestViewPanel drag-drop AND file input */
  const handleIngest = useCallback((result: IngestResult) => {
    setParseError(result.error ?? '');
    if (result.manifest) {
      setManifest(result.manifest);
      setShowUploadArea(false);
      setExportStep('done');
    }
  }, []);

  function handleDownload() {
    if (!manifest) return;
    const json = JSON.stringify(manifest, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scope-manifest-${manifest.root_scope.scope_namespace}-${manifest.root_scope.scope_key}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    if (!manifest) { setGeneratedSQL(''); return; }
    generateMigrationSQL(manifest).then(setGeneratedSQL).catch(() => setGeneratedSQL('-- Error generating SQL'));
  }, [manifest]);

  function handleCopySQL() {
    navigator.clipboard.writeText(generatedSQL);
    setSqlCopied(true);
    setTimeout(() => setSqlCopied(false), 2000);
  }

  function handleDownloadSQL() {
    if (!manifest || !generatedSQL) return;
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const blob = new Blob([generatedSQL], { type: 'text/sql' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scope-migration-${manifest.root_scope.scope_namespace}-${ts}.sql`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const targetBffUrl = useMemo(() => {
    const env = ENVIRONMENTS.find((e) => e.key === targetEnv);
    return env?.bff_base_url ?? null;
  }, [targetEnv]);

  const sourceBffUrl = useMemo(() => {
    const env = ENVIRONMENTS.find((e) => e.key === sourceEnv);
    return env?.bff_base_url ?? null;
  }, [sourceEnv]);

  /* ── Auto-reconnect when manifest is ready and a cached session exists ── */
  // Eliminates the manual "Reconnect" click.  The hook's initiate() will
  // silently refresh localStorage tokens; if the server-side refresh fails
  // but the device has approval history, the new device code is auto-approved.
  useEffect(() => {
    if (
      manifest &&
      targetBffUrl &&
      deviceAuth.phase === 'idle' &&
      deviceAuth.hasCachedSession(targetBffUrl, 'scopes.migrate')
    ) {
      deviceAuth.initiate(targetBffUrl, 'scopes.migrate');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest, targetBffUrl]);

  /* ── Auto-reconnect pull-target when the panel has a cached session ── */
  // Skip when service auth is available — no device auth needed.
  // Pull browses TARGET env (the actual remote), not source (which is local).
  useEffect(() => {
    if (serviceAuthAvailable) return;
    if (
      targetBffUrl &&
      pullDeviceAuth.phase === 'idle' &&
      pullDeviceAuth.hasCachedSession(targetBffUrl, 'scopes.migrate')
    ) {
      pullDeviceAuth.initiate(targetBffUrl, 'scopes.migrate');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetBffUrl, serviceAuthAvailable]);

  // ── Remote Snapshot (browse before pulling) ─────────────────────────
  // Browses the TARGET env (the actual remote) — not source (which is local).
  const snapshot = useRemoteSnapshot({
    remoteUrl: targetBffUrl,
    sourceEnv: targetEnv,
    tenantId: activeTenantId,
    accessToken: pullDeviceAuth.accessToken,
    onRefreshAuth: pullDeviceAuth.refreshAccessToken,
  });

  /* ── Auto-save grant to BFF after successful device auth ──────────── */
  // Persists the access/refresh tokens so the BFF can autonomously
  // resolve auth on future snapshot requests without user interaction.
  // Guarded: only fires when a snapshot was already successfully browsed
  // (proving the local session is alive), preventing 401 cascade on mount.
  const grantSavedRef = useRef(false);
  useEffect(() => {
    // Reset when env changes so we re-save for new pairings
    grantSavedRef.current = false;
  }, [targetEnv]);
  useEffect(() => {
    if (grantSavedRef.current) return; // already saved for this pairing
    if (
      pullDeviceAuth.phase === 'approved' &&
      pullDeviceAuth.accessToken &&
      targetBffUrl &&
      targetEnv &&
      snapshot.phase === 'ready' // only after a successful snapshot (session proven alive)
    ) {
      grantSavedRef.current = true;
      upsertGrant({
        remote_url: targetBffUrl,
        remote_env: targetEnv,
        tenant_id: activeTid ?? null,
        grant_type: 'device_bearer',
        access_token: pullDeviceAuth.accessToken,
        refresh_token: pullDeviceAuth.refreshToken ?? null,
        refresh_endpoint: `${targetBffUrl.replace(/\/+$/, '')}/bff/oauth/token`,
        ttl_seconds: DEFAULT_GRANT_TTL,
        auto_renew: true,
        token_expires_in: 600,
      }).catch(() => { /* grant save is best-effort */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pullDeviceAuth.phase, pullDeviceAuth.accessToken, targetBffUrl, targetEnv, snapshot.phase]);

  /* ── Auto-open popup for device auth verification URI ─────────────── */
  // When the device code is awaiting user approval, auto-open the
  // verification page in a popup so the user doesn't have to copy/paste.
  // We construct a LOCAL verification URL with a `target` param so the
  // approval is proxied to the remote BFF that holds the device code.
  const authPopupRef = useRef<Window | null>(null);
  useEffect(() => {
    if (
      pullDeviceAuth.phase === 'awaiting_approval' &&
      pullDeviceAuth.userCode
    ) {
      // Only open if we haven't already opened one (or it was closed)
      if (!authPopupRef.current || authPopupRef.current.closed) {
        const localUrl = targetBffUrl
          ? `/device?user_code=${encodeURIComponent(pullDeviceAuth.userCode)}&target=${encodeURIComponent(targetBffUrl)}`
          : (pullDeviceAuth.verificationUri || `/device?user_code=${encodeURIComponent(pullDeviceAuth.userCode)}`);
        authPopupRef.current = window.open(
          localUrl,
          'pac-auth-pull',
          'popup,width=500,height=600',
        );
      }
    }
    if (pullDeviceAuth.phase === 'approved' || pullDeviceAuth.phase === 'error') {
      // Close popup once auth completes
      if (authPopupRef.current && !authPopupRef.current.closed) {
        authPopupRef.current.close();
      }
      authPopupRef.current = null;
    }
  }, [pullDeviceAuth.phase, pullDeviceAuth.userCode, pullDeviceAuth.verificationUri, targetBffUrl]);

  // Reset snapshot when env/tenant changes
  useEffect(() => {
    snapshot.clearSnapshot();
    setShowSnapshotBrowser(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetEnv, activeTenantId]);

  const handleBrowseRemote = useCallback(() => {
    setShowSnapshotBrowser(true);
    if (snapshot.phase === 'idle' || snapshot.phase === 'error') {
      snapshot.captureSnapshot();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot.phase]);

  // Auto-recapture snapshot after a successful reconnect while browser is open.
  // When the user clicks "Reconnect" we clear the snapshot (→ idle), then after
  // device auth approves, this effect auto-fires captureSnapshot with the fresh
  // token so the user doesn't have to manually click "Take Snapshot" again.
  const reconnectCaptureRef = useRef(false);
  useEffect(() => {
    if (pullDeviceAuth.phase !== 'approved') {
      reconnectCaptureRef.current = false;
      return;
    }
    if (
      showSnapshotBrowser &&
      snapshot.phase === 'idle' &&
      pullDeviceAuth.accessToken &&
      !reconnectCaptureRef.current
    ) {
      reconnectCaptureRef.current = true;
      snapshot.captureSnapshot();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pullDeviceAuth.phase, pullDeviceAuth.accessToken, showSnapshotBrowser, snapshot.phase]);

  const handlePullFromSnapshot = useCallback(() => {
    const scopeIds = Array.from(snapshot.selectedScopeIds);
    if (scopeIds.length === 0 || !targetBffUrl) return;
    pullJob.create({
      source_env: targetEnv,
      root_scope_ids: scopeIds,
      include_outcomes: true,
      include_hot_stream: true,
      remote_url: targetBffUrl,
      ...(pullDeviceAuth.accessToken ? { access_token: pullDeviceAuth.accessToken } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot.selectedScopeIds, targetEnv, targetBffUrl, pullDeviceAuth.accessToken]);

  function handlePairRemote() {
    if (!targetBffUrl) return;
    setPushResult(null);
    setPushError(null);
    deviceAuth.initiate(targetBffUrl, 'scopes.migrate');
  }

  function handlePairPullRemote() {
    if (!targetBffUrl) return;
    pullJob.clear();
    snapshot.clearSnapshot();                       // reset stale error so UI returns to idle
    pullDeviceAuth.initiate(targetBffUrl, 'scopes.migrate');
  }

  /** Load a pulled manifest into the viewer (same as ingest). */
  function handlePullLoadManifest(m: ScopeManifest) {
    setManifest(m);
    setExportStep('done');
    pullJob.clear();
  }

  async function handlePushManifest() {
    const payload = filteredManifest;
    if (!payload || !deviceAuth.accessToken || !targetBffUrl) return;
    setIsPushing(true);
    setPushResult(null);
    setPushError(null);
    const doPush = (token: string) => {
      const remote = createRemoteBffClient({ origin: targetBffUrl!, accessToken: token });
      // Queue for admin approval on the remote — never auto-import.
      return remote.post<QueueResult>(`/orchestrator/migrate/scopes/queue`, payload);
    };
    try {
      const result = await doPush(deviceAuth.accessToken);
      // Map queue result to a push-friendly shape for the UI.
      setPushResult({
        dry_run: false, strategy: pushStrategy,
        scopes_created: 0, scopes_skipped: 0, scopes_existing: 0,
        outcomes_written: 0, outcomes_updated: 0, outcomes_skipped: 0, outcomes_deleted: 0,
        gcs_files_copied: 0, hot_stream_written: 0, hot_archive_files_copied: 0,
        id_map: {},
        _queued: true, _queue_id: result.id, _queue_message: result.message,
      } as any);
    } catch (err: any) {
      if (err instanceof RemoteBffError && err.status === 401) {
        const freshToken = await deviceAuth.refreshAccessToken();
        if (freshToken) {
          try {
            const result = await doPush(freshToken);
            setPushResult({
              dry_run: false, strategy: pushStrategy,
              scopes_created: 0, scopes_skipped: 0, scopes_existing: 0,
              outcomes_written: 0, outcomes_updated: 0, outcomes_skipped: 0, outcomes_deleted: 0,
              gcs_files_copied: 0, hot_stream_written: 0, hot_archive_files_copied: 0,
              id_map: {},
              _queued: true, _queue_id: result.id, _queue_message: result.message,
            } as any);
            return;
          }
          catch (retryErr: any) { setPushError(retryErr?.body?.error_description || retryErr?.body?.error || retryErr?.message || String(retryErr)); return; }
        }
        deviceAuth.disconnect();
        setPushError('Device session expired. Please re-pair to push again.');
        return;
      }
      setPushError(err?.body?.error_description || err?.body?.error || err?.message || String(err));
    } finally { setIsPushing(false); }
  }

  const canExport = selection.selectedScopeIds.size > 0 && !!sourceEnv && !exportMutation.isPending;
  const errorMsg = (exportMutation.error as any)?.message;
  const getEnvDef = (key: string): EnvironmentDef | undefined => ENVIRONMENTS.find((e) => e.key === key);

  const previewSummary = useMemo(
    () => computePreviewSummary(selection.allSelectedScopes, selection.allCachedScopes),
    [selection.allSelectedScopes, selection.allCachedScopes],
  );

  // Reset selection when tenant changes via sidebar
  useEffect(() => {
    selection.resetSelection();
    setManifest(null);
  }, [activeTenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-full min-h-0 flex flex-col gap-3 overflow-hidden">
      <div className="flex items-center justify-between gap-4 shrink-0">
        <div className="flex items-center gap-4">
          <EnvDropdown label="Source" value={sourceEnv} onChange={setSourceEnv} />
          <span className="text-neutral-700">→</span>
          <EnvDropdown label="Target" value={targetEnv} onChange={setTargetEnv} />
        </div>
        <div className="flex items-center gap-1">
          <StepPill step={1} label="Select" active={exportStep === 'select'} done={exportStep !== 'select'} />
          <span className="text-neutral-700 text-[10px]">›</span>
          <StepPill step={2} label="Preview" active={exportStep === 'preview'} done={exportStep === 'done'} />
          <span className="text-neutral-700 text-[10px]">›</span>
          <StepPill step={3} label="Export" active={false} done={exportStep === 'done'} />
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1.5 rounded border border-neutral-700 text-xs text-neutral-300 hover:border-neutral-500 flex items-center gap-1.5"
            onClick={() => setShowUploadArea((v) => !v)}
          >
            <FileJson className="w-3.5 h-3.5" />
            Load JSON
          </button>
        </div>
      </div>

      {showUploadArea && (
        <div className="border border-neutral-800 rounded p-3 space-y-2 shrink-0">
          <div className="flex items-center gap-3">
            <button
              className="px-3 py-1.5 rounded border border-neutral-700 text-xs text-neutral-200 hover:border-neutral-500"
              onClick={() => fileRef.current?.click()}
            >
              Upload file
            </button>
            <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={handleFileUpload} />
            <span className="text-[10px] text-neutral-500">or paste below</span>
          </div>
          <textarea
            value={manifestText}
            onChange={(e) => tryParseManifest(e.target.value)}
            placeholder='{"version":1,"scopes":[...]}'
            rows={4}
            className="w-full px-2 py-1.5 rounded pac-glass-subtle text-[11px] text-neutral-200 placeholder:text-neutral-700 font-mono resize-y"
          />
          {parseError && <div className="text-xs text-rose-400">{parseError}</div>}
        </div>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-2 gap-3">
        <div className="min-h-0 flex flex-col border border-neutral-800 rounded-md overflow-hidden relative">
          <SourceScopesPanel
            activeTenantId={activeTenantId}
            tenantList={tenantList}
            namespaces={selection.namespaces}
            selectedNs={selection.selectedNs}
            onSelectNs={selection.setSelectedNs}
            nsSelectionState={selection.nsSelectionState}
            onToggleNamespaceScopes={selection.toggleNamespaceScopes}
            allNsSelectState={selection.allNsSelectState}
            onToggleAllNamespaces={selection.toggleAllNamespaces}
            loadingNs={selection.loadingNs}
            nsSearch={selection.nsSearch}
            onNsSearchChange={selection.setNsSearch}
            nsLoading={selection.nsLoading}
            keySearch={selection.keySearch}
            onKeySearchChange={selection.setKeySearch}
            filteredScopes={selection.filteredScopes}
            selectedScopeIds={selection.selectedScopeIds}
            onToggleScopeId={selection.toggleScopeId}
            onToggleAllScopes={selection.toggleAllScopes}
            allFilteredSelected={selection.allFilteredSelected}
            someFilteredSelected={selection.someFilteredSelected}
            keysLoading={selection.keysLoading}
            selectedCount={selection.selectedScopeIds.size}
            totalScopeCount={selection.totalScopeCount}
            namespacesRawCount={selection.namespacesRaw.length}
            onClearSelection={() => selection.setSelectedScopeIds(new Set())}
            exportStep={exportStep}
            onSetExportStep={setExportStep}
            canExport={canExport}
            exportPending={exportMutation.isPending}
            onExport={() => exportMutation.mutate()}
            manifest={manifest}
            onShowExportSummary={() => setShowExportSummary(true)}
          />

          {exportStep === 'preview' && previewSummary && (
            <ExportPreviewOverlay
              previewSummary={previewSummary}
              sourceEnv={sourceEnv}
              targetEnv={targetEnv}
              getEnvDef={getEnvDef}
              rootScopeIds={selection.allSelectedScopes.map((s) => s.id_scope!).filter(Boolean)}
              tenantId={typeof activeTenantId === 'number' ? activeTenantId : 0}
              includeChildren={includeChildren}
              setIncludeChildren={setIncludeChildren}
              filterMaxDepth={filterMaxDepth}
              setFilterMaxDepth={setFilterMaxDepth}
              includeOutcomes={includeOutcomes}
              setIncludeOutcomes={setIncludeOutcomes}
              includeHotStream={includeHotStream}
              setIncludeHotStream={setIncludeHotStream}
              canExport={canExport}
              exportPending={exportMutation.isPending}
              onExport={() => exportMutation.mutate()}
              onBack={() => setExportStep('select')}
            />
          )}

          {showExportSummary && exportSummary && (
            <ExportSummaryPopup
              summary={exportSummary}
              onDismiss={() => setShowExportSummary(false)}
              onNewExport={() => {
                setShowExportSummary(false);
                setExportStep('select');
                selection.setSelectedScopeIds(new Set());
                setManifest(null);
                setManifestText('');
              }}
            />
          )}
        </div>

        <div className="min-h-0 flex flex-col border border-neutral-800 rounded-md overflow-hidden relative">
          <ManifestViewPanel
            manifest={manifest}
            cloneResult={cloneResult}
            setCloneResult={setCloneResult}
            targetTenantId={targetTenantId}
            onDownload={handleDownload}
            onClear={() => { setManifest(null); setManifestText(''); setParseError(''); }}
            viewTab={viewTab}
            setViewTab={setViewTab}
            generatedSQL={generatedSQL}
            sqlCopied={sqlCopied}
            onCopySQL={handleCopySQL}
            onDownloadSQL={handleDownloadSQL}
            manifestSelection={manifestSelection}
            setManifestSelection={setManifestSelection}
            onToggleManifestKey={handleToggleManifestKey}
            onToggleManifestNamespace={handleToggleManifestNamespace}
            onIngest={handleIngest}
            parseError={parseError}
            onApplyToTenant={() => {
              setPendingApprovalId(null);
              setPendingApprovalManifest(null);
              setCloneDryRunResult(null);
              // Default target to the active sidebar tenant (most common use case)
              setTargetTenantId(activeTenantId);
              setShowImportApproval(true);
            }}
          />

          {/* Pending inbound imports awaiting approval */}
          <PendingImportsPanel
            items={pendingImports}
            isLoading={pendingLoading}
            tenantList={tenantList}
            onReview={handlePendingReview}
            reviewLoading={pendingReviewLoading}
            onApprove={handlePendingApprove}
            onReject={handlePendingReject}
            rejectLoading={pendingRejectLoading}
          />

          {/* Pull from remote — async job-based manifest fetch */}
          <PullFromRemoteSection
            sourceEnv={targetEnv}
            getEnvDef={getEnvDef}
            sourceBffUrl={targetBffUrl}
            activeTenantId={activeTenantId}
            serviceAuthAvailable={serviceAuthAvailable}
            devicePhase={pullDeviceAuth.phase}
            userCode={pullDeviceAuth.userCode}
            verificationUri={pullDeviceAuth.verificationUri}
            expiresIn={pullDeviceAuth.expiresIn}
            deviceError={pullDeviceAuth.error}
            hasCachedSession={pullDeviceAuth.hasCachedSession}
            accessToken={pullDeviceAuth.accessToken}
            onPairRemote={handlePairPullRemote}
            onCancelAuth={() => pullDeviceAuth.cancel()}
            onResetAuth={() => pullDeviceAuth.reset()}
            onDisconnect={() => pullDeviceAuth.disconnect()}
            activeJobId={pullJob.activeJobId}
            job={pullJob.job}
            isPolling={pullJob.isPolling}
            isCreating={pullJob.isCreating}
            pullManifest={pullJob.manifest}
            pullError={pullJob.error}
            pullProgress={pullJob.progress}
            pullElapsedMs={pullJob.elapsedMs}
            pullTimedOut={pullJob.isTimedOut}
            onCreatePull={pullJob.create}
            onCancelPull={pullJob.cancel}
            onClearPull={pullJob.clear}
            onLoadManifest={handlePullLoadManifest}
            selectedRootScopeIds={selection.allSelectedScopes.map((s) => s.id_scope!).filter(Boolean)}
            previewSummary={previewSummary}
            onBrowseRemote={handleBrowseRemote}
            snapshotActive={showSnapshotBrowser}
          />

          {/* Remote snapshot browser — delta-aware scope discovery */}
          {showSnapshotBrowser && (
            <RemoteSnapshotBrowser
              sourceEnvLabel={getEnvDef(targetEnv)?.label ?? targetEnv}
              phase={snapshot.phase}
              error={snapshot.error}
              errorCode={snapshot.errorCode}
              diagnostics={snapshot.diagnostics}
              diagnosing={snapshot.diagnosing}
              onRunDiagnostics={snapshot.runDiagnostics}
              snapshotId={snapshot.snapshotId}
              capturedAt={snapshot.capturedAt}
              namespaces={snapshot.namespaces}
              localOnlyNamespaces={snapshot.localOnlyNamespaces}
              namespaceSummary={snapshot.namespaceSummary}
              selectedNs={snapshot.selectedNs}
              onSelectNs={snapshot.setSelectedNs}
              nsSearch={snapshot.nsSearch}
              onNsSearchChange={snapshot.setNsSearch}
              filteredNamespaces={snapshot.filteredNamespaces}
              scopeKeys={snapshot.scopeKeys}
              localOnlyScopes={snapshot.localOnlyScopes}
              scopeDelta={snapshot.scopeDelta}
              scopesLoading={snapshot.scopesLoading}
              keySearch={snapshot.keySearch}
              onKeySearchChange={snapshot.setKeySearch}
              filteredScopes={snapshot.filteredScopes}
              selectedScopeIds={snapshot.selectedScopeIds}
              onToggleScopeId={snapshot.toggleScopeId}
              onToggleAllScopes={snapshot.toggleAllScopes}
              onSelectAllNew={snapshot.selectAllNew}
              allFilteredSelected={snapshot.allFilteredSelected}
              someFilteredSelected={snapshot.someFilteredSelected}
              selectedNewCount={snapshot.selectedNewCount}
              selectedSyncedCount={snapshot.selectedSyncedCount}
              totalSelectedCount={snapshot.totalSelectedCount}
              onCaptureSnapshot={snapshot.captureSnapshot}
              onClearSnapshot={() => {
                snapshot.clearSnapshot();
                setShowSnapshotBrowser(false);
              }}
              onReconnect={handlePairPullRemote}
              onPullSelected={handlePullFromSnapshot}
              canPull={snapshot.selectedScopeIds.size > 0 && !pullJob.isCreating}
            />
          )}

          {manifest && (
            <PushToRemoteSection
              manifest={manifest}
              targetEnv={targetEnv}
              getEnvDef={getEnvDef}
              targetBffUrl={targetBffUrl}
              devicePhase={deviceAuth.phase}
              userCode={deviceAuth.userCode}
              verificationUri={deviceAuth.verificationUri}
              expiresIn={deviceAuth.expiresIn}
              deviceError={deviceAuth.error}
              hasCachedSession={deviceAuth.hasCachedSession}
              onPairRemote={handlePairRemote}
              onCancelAuth={() => deviceAuth.cancel()}
              onResetAuth={() => deviceAuth.reset()}
              onDisconnect={() => deviceAuth.disconnect()}
              pushStrategy={pushStrategy}
              setPushStrategy={setPushStrategy}
              pushResult={pushResult}
              setPushResult={setPushResult}
              pushError={pushError}
              setPushError={setPushError}
              isPushing={isPushing}
              filteredManifest={filteredManifest}
              manifestSelectionSize={manifestSelection.size}
              onPushManifest={handlePushManifest}
            />
          )}

          {showImportApproval && (pendingApprovalManifest || filteredManifest) && (
            <ImportApprovalPopup
              filteredManifest={(pendingApprovalManifest ?? filteredManifest)!}
              tenantList={tenantList}
              activeTenantId={activeTenantId}
              targetTenantId={targetTenantId}
              setTargetTenantId={setTargetTenantId}
              strategy={cloneStrategy}
              setStrategy={setCloneStrategy}
              dryRunResult={cloneDryRunResult}
              dryRunPending={pendingApprovalId ? pendingApprovalDryRunMutation.isPending : importDryRunMutation.isPending}
              dryRunError={
                (pendingApprovalId ? pendingApprovalDryRunMutation.isError : importDryRunMutation.isError)
                  ? (((pendingApprovalId ? pendingApprovalDryRunMutation.error : importDryRunMutation.error) as any)?.message ?? 'Dry-run failed')
                  : null
              }
              onDryRun={() => {
                setCloneDryRunResult(null);
                pendingApprovalId ? pendingApprovalDryRunMutation.mutate() : importDryRunMutation.mutate();
              }}
              importPending={pendingApprovalId ? pendingApprovalConfirmMutation.isPending : importConfirmMutation.isPending}
              onConfirm={() => {
                pendingApprovalId ? pendingApprovalConfirmMutation.mutate() : importConfirmMutation.mutate();
              }}
              onClose={() => {
                setShowImportApproval(false);
                setCloneDryRunResult(null);
                setPendingApprovalId(null);
                setPendingApprovalManifest(null);
              }}
            />
          )}

          {/* Progress overlay — renders above everything during long imports */}
          {progressManifest && (progressActive || progressResult || progressError) && (
            <ImportProgressOverlay
              isActive={progressActive}
              manifest={progressManifest}
              strategy={cloneStrategy}
              targetTenantLabel={
                (() => {
                  const t = tenantList.find((t) => t.idTenant === targetTenantId);
                  return t ? t.tenantName : `Tenant ${targetTenantId}`;
                })()
              }
              result={progressResult}
              error={progressError}
              onDismiss={() => {
                setProgressManifest(null);
                setProgressResult(null);
                setProgressError(null);
              }}
            />
          )}
        </div>
      </div>

      {errorMsg && (
        <div className="text-xs text-rose-400 border border-rose-900/50 bg-rose-950/20 rounded p-2 shrink-0">
          {String(errorMsg)}
        </div>
      )}
    </div>
  );
}
